/**
 * Icon resolver for backend-defined recommendation agents.
 *
 * The backend `AGENT_REGISTRY` (server/recommendations/pipeline/registry.py)
 * declares each agent's icon as a Lucide-react component name (string). The
 * frontend resolves that string to the actual component here, in one place,
 * so adding a new agent server-side requires adding its icon component to
 * the map below — and only here.
 *
 * Falls back to `Sparkle` for unknown icon names. This keeps the UI from
 * crashing if a backend change ships ahead of a frontend deploy, while
 * still surfacing the mismatch visibly.
 */

import {
  PiBell,
  PiCalendarCheck,
  PiCoffee,
  PiEnvelope,
  PiHandshake,
  PiSparkle,
  PiUserPlus,
} from 'react-icons/pi'
import type { IconType } from 'react-icons'

const AGENT_ICON_MAP: Record<string, IconType> = {
  Mail: PiEnvelope,
  Coffee: PiCoffee,
  // The Deal Updates agent (update_deal) ships `icon="ArrowRight"` from the
  // backend registry; product wants a handshake for deals. Long-term fix is to
  // rename the backend icon to "Handshake" and key this map on that — until
  // then, ArrowRight (used only by the deals agent) resolves to the handshake.
  ArrowRight: PiHandshake,
  CalendarClock: PiCalendarCheck,
  Bell: PiBell,
  UserPlus: PiUserPlus,
}

/**
 * Resolve a Phosphor icon component for a backend-supplied agent icon name.
 *
 * @param iconName - Icon name from the server's AgentConfig.icon field
 * @returns The matching Phosphor component, or Sparkle as a visible fallback
 */
export function resolveAgentIcon(iconName: string): IconType {
  return AGENT_ICON_MAP[iconName] ?? PiSparkle
}
